import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import theme from "../../../theme";
import { resetUserSettings } from "../../../redux/features/userSettingsSlice";
import {
  resetUserDetails,
  setIsFromSettings,
  setCanExtendTrial,
  setIsStoppedNativusCoachSubscription,
} from "../../../redux/features/userSlice";
import { removeToken } from "../../../storage/user";
import { SUCCESS } from "../../../utils/constants";
import { ErrorMessage } from "../../../utils/commonUtils";
import { SignInRoute } from "../../route/routes";
import { AuthorizationActions, SettingsActions } from "../../api/RequestUtil";
import { generateToken } from "../../../notifications/firebase";
import AlertService from "../../common/Alert/alertService";
import CustomDialog from "../../common/CustomDialog";
import AccountDetails from "./AccountDetails";
import SelectPlan from "./SelectPlan";
import UpdateBilling from "./UpdateBilling";

const ChangePlanStoppedSubscription = ({ open = false, handleClose, refetchData = () => {} }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { warning, error, success } = AlertService();
  const {
    currentPlan: { planId },
    accountOwnerDetails: { firstName, lastName, email, phone, companyName, address },
  } = useSelector((state) => state.settings);
  const { isRiskFreeTier, canExtendTrial } = useSelector((state) => state.user);

  const [selectedPlanId, setSelectedPlanId] = useState(planId);
  const [currentStepCount, setCurrentStepCount] = useState(0);
  const [loader, setLoader] = useState(false);
  const [updateBilling, setUpdateBilling] = useState(false);
  const [extendingTrial, setExtendingTrial] = useState(false);
  const [validatedCodeData, setValidatedCodeData] = useState(null);

  const handleCloseUpdateBilling = () => {
    setUpdateBilling(false);
    refetchData(); // Refetch account data in parent if needed
  };

  // Submit user details to Marketo form for eWay onboarding
  const submitToMktoForms = () => {
    try {
      // Extract country from address
      let country = "";
      if (address) {
        const addressParts = address.split(",");
        country = addressParts[addressParts.length - 1].trim();
      }

      // Check if MktoForms2 is available
      if (!window.MktoForms2) {
        return;
      }

      if (country === "New Zealand" || country === "Australia") {
        const allForms = window.MktoForms2.allForms();
        if (!allForms || allForms.length === 0) {
          return;
        }

        const myForm = allForms[0];

        myForm.addHiddenFields({
          FirstName: firstName,
          LastName: lastName,
          Email: email,
          Phone: phone,
          Company: companyName,
          eWAY_Branch__c: country,
        });

        window.MktoForms2.whenReady((myForm2) => {
          myForm2.onSuccess(() => false); // Prevent redirect

          myForm2.submit();
        });
      }
    } catch (err) {
      console.error("❌ Error submitting to Marketo forms:", err);
    }
  };

  const onLogout = async () => {
    const device_id = localStorage.getItem("device_id");
    const fcmToken = localStorage.getItem("fcm_token");
    if (fcmToken && device_id) {
      await destroyNotificationToken(device_id);
    }
    removeToken();
    dispatch(resetUserDetails());
    dispatch(resetUserSettings());
    navigate(SignInRoute);
    setTimeout(() => {
      generateToken();
    }, 2000);
  };

  const destroyNotificationToken = (device_id) => {
    AuthorizationActions.deleteTokenFromServer({ device_id })
      .then(() => {})
      .catch((err) => {
        console.log(err);
      });
  };

  const checkPaymentToken = async () => {
    try {
      const { data } = await SettingsActions.getAccountOwnerDetails();
      // Check if user has payment_token in the response
      return !!data?.data?.userDetails?.payment_token;
    } catch (err) {
      console.error("Error checking payment token:", err);
      return false;
    }
  };

  const handleExtendTrial = async () => {
    setExtendingTrial(true);
    try {
      const { data } = await AuthorizationActions.extendTrial();
      if (data.status === SUCCESS) {
        success(data.message || "Your trial has been extended by 7 days!");
        // Update Redux state to reflect trial is no longer extendable and subscription is active
        dispatch(setCanExtendTrial(false));
        dispatch(setIsStoppedNativusCoachSubscription(false));

        // Small delay to ensure backend transaction completes
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Force complete page reload to clear all cached state
        window.location.reload();
      }
    } catch (err) {
      error(ErrorMessage(err));
    } finally {
      setExtendingTrial(false);
    }
  };

  const changePlanSteps = [
    {
      step: 0,
      label: "Reactivate your account - Please select a New Plan",
      component: (
        <SelectPlan
          selectedPlanId={selectedPlanId}
          setSelectedPlanId={setSelectedPlanId}
          onValidatedCodeChange={setValidatedCodeData}
        />
      ),
      onNextClick: async () => {
        if (!selectedPlanId) {
          warning("Please select any of plan");
          return;
        }

        // Check if payment token exists in the database
        const hasPaymentToken = await checkPaymentToken();
        if (!hasPaymentToken) {
          warning("Please add your credit card details before reactivating your subscription.");
          dispatch(setIsFromSettings(true));
          setUpdateBilling(true); // Open Update Billing modal
          return;
        }

        if (selectedPlanId) setCurrentStepCount(currentStepCount + 1);
      },
      onPreviousClick: () => {
        onLogout();
      },
      cancelTitle: "Logout",
      saveTitle: "Next",
    },
    {
      step: 1,
      label: "New Plan - Your New Payment Schedule",
      component: <AccountDetails selectedPlanId={selectedPlanId} />,
      onNextClick: () => {
        setLoader(true);

        // Capture isRiskFreeTier BEFORE any Redux updates that might clear it
        const wasRiskFreeTier = isRiskFreeTier;

        // Check if selecting unlocked plan
        const isSelectingUnlockedPlan = validatedCodeData && validatedCodeData.plan.id === selectedPlanId;

        SettingsActions.changePlan({
          new_plan_id: selectedPlanId,
          ...(isSelectingUnlockedPlan && { code_id: validatedCodeData.codeId }),
        })
          .then(async ({ data }) => {
            if (data.status === SUCCESS) {
              success(data.message || "Plan reactivated successfully");

              // If upgrading from Risk Free Trial, submit to Marketo for eWay onboarding
              // Use captured value since Redux state will be updated
              if (wasRiskFreeTier) {
                submitToMktoForms();
              }

              // Update Redux state to reflect subscription is now active
              dispatch(setIsStoppedNativusCoachSubscription(false));

              // Close modal immediately
              if (handleClose) {
                handleClose();
              }

              // Wait for redux-persist to flush state to localStorage before reloading
              await new Promise((resolve) => setTimeout(resolve, 500));

              // Force complete page reload to clear all cached state
              window.location.reload();
            }
          })
          .catch((data) => error(ErrorMessage(data)))
          .finally(() => setLoader(false));
      },
      onPreviousClick: () => setCurrentStepCount(currentStepCount - 1),
      cancelTitle: "Cancel",
      saveTitle: "Confirm",
    },
  ];

  const currentStep = changePlanSteps[currentStepCount];

  // Show "Extend Trial" button only on step 0 for eligible users
  const showExtendTrialButton = currentStepCount === 0 && isRiskFreeTier && canExtendTrial;

  return (
    <>
      <CustomDialog
        title={currentStep.label}
        open={open}
        handleClose={currentStep.onPreviousClick}
        dialogWidth={currentStepCount === 0 ? "lg" : "md"}
        handleSaveTitle={currentStep.saveTitle}
        handleCancelTitle={currentStep.cancelTitle}
        handleSave={currentStep.onNextClick}
        reverseButtons
        loader={loader}
        displayCloseIcon={false}
        isThirdButtonExist={showExtendTrialButton}
        handleThirdButtonOnClick={handleExtendTrial}
        thirdButtonText="Extend Trial by 7 Days"
        thirdButtonLoader={extendingTrial}
        thirdButtonColor={theme.palette.info.main}
      >
        {currentStep.component}
      </CustomDialog>

      <UpdateBilling
        open={updateBilling}
        handleClose={handleCloseUpdateBilling}
        refetchData={handleCloseUpdateBilling}
        setChangePlan={(shouldShow) => {
          if (shouldShow) {
            // After card details saved, close UpdateBilling and advance to step 2
            setUpdateBilling(false);
            setCurrentStepCount(1); // Move to confirmation schedule step
          }
        }}
        showChangePlanAfterUpdateBilling={true}
      />
    </>
  );
};

ChangePlanStoppedSubscription.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetchData: PropTypes.func,
};

export default ChangePlanStoppedSubscription;
